/**
 * The digital bill — ported from the mobile app (Mobile/src/screens/account/
 * BillScreen.js). A receipt the customer can hold up at the door: the order,
 * what it cost, and a Code 128 barcode the shop scans to confirm it is real.
 * The barcode carries the order id; verification is the admin fetching that
 * order from the server (VerifyAdminTab), so the symbol proves nothing alone.
 *
 * Dressed as paper because that is what it stands in for: it prints out of a
 * slot, torn at both ends, and the barcode is printed last. "Save" prints
 * just the receipt, which every browser can also save as a PDF.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { T, num, LOCALE } from './i18n';
import { Barcode, billCodeFor } from './barcode';
import { paymentKind } from './payment';
import { orderMoney, customerName, statusKey, DATE_FORMAT } from './orderView';

const STATUS = { pending: 'Pending', processing: 'Processing', in_progress: 'In progress', shipped: 'Shipped', out_for_delivery: 'Out for delivery', delivered: 'Delivered', completed: 'Completed', cancelled: 'Cancelled', refunded: 'Refunded' };

const CSS = `
.lxb-back{position:fixed;inset:0;z-index:600;background:rgba(7,7,8,0.82);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);overflow-y:auto;padding:calc(28px + env(safe-area-inset-top)) 16px calc(40px + env(safe-area-inset-bottom));animation:lxbFade .3s ease both}
@keyframes lxbFade{from{opacity:0}to{opacity:1}}
.lxb-col{width:100%;max-width:340px;margin:0 auto;display:flex;flex-direction:column;align-items:center}
.lxb-head{width:100%;display:flex;align-items:center;gap:12px;margin-bottom:18px;color:#F7F1E6}
.lxb-head h2{flex:1;font-family:'Cormorant Garamond','Alexandria',Georgia,serif;font-style:italic;font-weight:300;font-size:32px}
.lxb-x{width:44px;height:44px;border-radius:50%;border:1px solid rgba(255,255,255,0.16);background:rgba(255,255,255,0.06);color:#F7F1E6;font-size:20px;display:flex;align-items:center;justify-content:center;cursor:pointer}
.lxb-slot{width:100%;height:14px;border-radius:7px;background:#1A1712;box-shadow:inset 0 -4px 0 #070708;position:relative;z-index:2}
.lxb-paperwrap{width:calc(100% - 16px);filter:drop-shadow(0 22px 34px rgba(0,0,0,0.45));margin-top:-6px}
.lxb-window{overflow:hidden;animation:lxbFeed var(--feed,1.4s) cubic-bezier(.25,.8,.35,1) both}
@keyframes lxbFeed{from{clip-path:inset(0 0 100% 0)}to{clip-path:inset(0 0 0 0)}}
.lxb-paper{background:#FFFCF6;color:#2A241D;padding:20px 22px 18px;font-family:'Alexandria',system-ui,sans-serif}
.lxb-edge{display:block;width:100%;height:12px}
.lxb-brand{text-align:center;margin-bottom:12px}
.lxb-kicker{font-size:10px;letter-spacing:4px;color:#6F6558;font-weight:300}
.lxb-receipt{font-family:'Cormorant Garamond','Alexandria',Georgia,serif;font-style:italic;font-weight:300;font-size:30px;color:#14110E;margin-top:2px}
.lxb-rule{border-top:1px dashed rgba(20,17,14,0.2);margin:12px 0}
.lxb-row{display:flex;justify-content:space-between;gap:12px;font-size:13px;color:#6F6558;margin:6px 0}
.lxb-row.strong{color:#14110E;font-weight:500}
.lxb-row b{font-weight:inherit;font-variant-numeric:tabular-nums;text-align:end}
.lxb-item{display:flex;align-items:flex-start;gap:10px;margin:8px 0;font-size:13px}
.lxb-qty{color:#8A6A2A;font-weight:500;min-width:22px}
.lxb-name{flex:1;min-width:0;color:#14110E}
.lxb-name small{display:block;color:#6F6558;font-size:11px;margin-top:2px}
.lxb-code{text-align:center;margin-top:16px;opacity:0;animation:lxbCode .45s ease var(--code,1.5s) forwards}
@keyframes lxbCode{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:none}}
.lxb-plate{background:#FFFFFF;border:1px solid rgba(20,17,14,0.08);border-radius:10px;padding:12px 18px 10px;display:inline-block;margin-top:8px}
.lxb-value{margin-top:6px;font-size:11px;letter-spacing:3px;color:#111111;font-weight:500;direction:ltr}
.lxb-foot{display:flex;justify-content:center;align-items:center;gap:6px;margin-top:14px;font-size:11px;color:#6F6558}
.lxb-actions{width:100%;display:flex;flex-direction:column;gap:8px;margin-top:22px}
.lxb-gold{height:58px;border-radius:29px;background:linear-gradient(100deg,#F0DDAE 0%,#C9A566 55%,#E8CF98 100%);color:#14110E;font-family:'Alexandria',sans-serif;font-weight:600;font-size:15px;border:none;cursor:pointer}
.lxb-done{height:52px;border-radius:26px;background:#F7F1E6;color:#070708;font-family:'Alexandria',sans-serif;font-weight:600;font-size:14px;border:none;cursor:pointer}
@media(prefers-reduced-motion:reduce){.lxb-window,.lxb-code{animation:none!important;opacity:1}}
/* Save = print just the receipt; every browser can print to PDF */
@media print{
  body *{visibility:hidden!important}
  .lxb-back{position:absolute;inset:auto;left:0;top:0;background:none;backdrop-filter:none;padding:0}
  .lxb-paperwrap,.lxb-paperwrap *{visibility:visible!important}
  .lxb-paperwrap{position:absolute;left:0;top:0;width:80mm;filter:none}
  .lxb-window,.lxb-code{animation:none!important;opacity:1!important;clip-path:none!important}
}
`;

/* A torn paper edge, drawn so it scales with the paper and can point either way. */
const TornEdge = ({ flip = false, teeth = 28 }) => {
  const W = 300, H = 12, step = W / teeth;
  let d = flip ? `M0 ${H} L${W} ${H} L${W} 5` : `M0 0 L${W} 0 L${W} 7`;
  for (let i = teeth; i > 0; i -= 1) {
    const a = (i - 0.5) * step, b = (i - 1) * step;
    d += flip ? ` L${a} 0 L${b} 5` : ` L${a} 12 L${b} 7`;
  }
  return <svg className="lxb-edge" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true"><path d={d + ' Z'} fill="#FFFCF6"/></svg>;
};

export default function BillView({ order, fmt, onClose }) {
  React.useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!order) return null;
  const code = billCodeFor(order);
  const items = order.items || [];
  /* The same reading of an order the app uses (orderView), so a missing
     delivery charge is derived rather than printed as 0 and the rows still
     add up to the total. */
  const { subtotal, discount, shipping, total } = orderMoney(order);
  const name = customerName(order);
  const sk = statusKey(order.status);
  const placed = order.created_at ? new Date(order.created_at).toLocaleDateString(LOCALE, DATE_FORMAT) : '—';
  /* Unknown payment methods are left off rather than guessed. */
  const pay = paymentKind(order.payment_method);
  /* a long bill takes longer to print than a short one — the point of watching it */
  const feed = Math.min(2.2, Math.max(0.9, 0.55 + items.length * 0.22));

  /* Rendered on <body>: the account pages animate in with a transform, which
     would make this overlay position against the page instead of the screen. */
  return createPortal(
    <div className="lxb-back" role="dialog" aria-modal="true" aria-label={T('Your bill')} onClick={onClose}>
      <style>{CSS}</style>
      <div className="lxb-col" onClick={e => e.stopPropagation()} style={{ '--feed': `${feed}s`, '--code': `${feed + 0.12}s` }}>
        <div className="lxb-head">
          <h2>{T('Your bill')}</h2>
          <button className="lxb-x" onClick={onClose} aria-label={T('Close')}>×</button>
        </div>
        <div className="lxb-slot" aria-hidden="true"/>
        <div className="lxb-paperwrap">
          <TornEdge flip/>
          <div className="lxb-window">
            <div className="lxb-paper">
              <div className="lxb-brand">
                <div className="lxb-kicker">{T('LUMIÈRE · DOHA')}</div>
                <div className="lxb-receipt">{T('Receipt')}</div>
              </div>
              <div className="lxb-rule"/>
              {name && <div className="lxb-row strong"><span>{T('Customer')}</span><b><bdi>{name}</bdi></b></div>}
              <div className="lxb-row strong"><span>{T('Order')}</span><b dir="ltr">{order.order_number || order.id}</b></div>
              <div className="lxb-row"><span>{T('Placed')}</span><b>{placed}</b></div>
              {pay && <div className="lxb-row"><span>{T('Payment')}</span><b>{pay === 'cod' ? T('Cash on Delivery') : T('Credit / Debit Card')}</b></div>}
              <div className="lxb-row"><span>{T('Status')}</span><b>{T((sk && STATUS[sk.replace('status.', '')]) || order.status || '—')}</b></div>
              <div className="lxb-rule"/>
              {items.map((it, i) => (
                <div key={`${it.product_id}-${i}`} className="lxb-item">
                  <span className="lxb-qty">{num(it.quantity)}×</span>
                  <span className="lxb-name">{it.product_name}{it.size_ml ? <small>{T('{n} ml', { n: num(it.size_ml) })}</small> : null}</span>
                  <b style={{ fontWeight: 500 }}>{fmt(Number(it.total_price ?? 0))}</b>
                </div>
              ))}
              <div className="lxb-rule"/>
              {subtotal != null && <div className="lxb-row"><span>{T('Subtotal')}</span><b>{fmt(subtotal)}</b></div>}
              {discount != null && discount > 0 && <div className="lxb-row"><span>{T('Discount')}{order.discount_code ? ` · ${order.discount_code}` : ''}</span><b>−{fmt(discount)}</b></div>}
              {shipping != null && shipping >= 0 && <div className="lxb-row"><span>{T('Shipping')}</span><b>{shipping > 0 ? fmt(shipping) : T('Free')}</b></div>}
              <div className="lxb-rule" style={{ borderTopStyle: 'solid', borderColor: 'rgba(20,17,14,0.3)' }}/>
              <div className="lxb-row strong" style={{ fontSize: 15 }}><span>{T('Total')}</span><b>{fmt(total != null ? total : 0)}</b></div>
              {/* the part the shop reads */}
              {code && <div className="lxb-code">
                <div className="lxb-kicker">{T('SCAN TO VERIFY')}</div>
                <div className="lxb-plate">
                  <Barcode value={code} width={220} height={60}/>
                  <div className="lxb-value">{code}</div>
                </div>
              </div>}
              <div className="lxb-foot"><span aria-hidden="true">⛨</span>{T('Verified by Lumière at the door')}</div>
            </div>
          </div>
          <TornEdge/>
        </div>
        <div className="lxb-actions">
          <button className="lxb-gold" onClick={() => window.print()}>{T('Save receipt')}</button>
          <button className="lxb-done" onClick={onClose}>{T('Done')}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
